import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import AttachmentInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/attachment-interface';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Job from '@/model/entity/job/job';
import JobDto from '@/model/entity/job/job-dto';
import JobService from '@/model/service/job/job-service';

@Component
export default class JobForm extends Vue
{
    @Prop()
    private job!: Job;

    @Prop({
        type: Boolean,
        default: false,
    })
    private cancelButton!: boolean;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;
    private fieldValues: FormFieldValuesInterface = {};

    private created()
    {
        this.generateFormFromJob();
    }

    @Watch('job')
    private onJobChanged()
    {
        this.generateFormFromJob();
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.job)
        {
            return;
        }

        // Generate a job from the form data
        const job = this.generateJobFromForm(form);

        // Emit a save event with the job
        this.$emit('save', job);
    }

    /**
     * Generates the form from the job
     */

    private generateFormFromJob()
    {
        if (!this.job)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    title: this.$t('general.field.basic_details').toString(),
                    fields: [
                        {
                            key: 'client',
                            type: 'integer',
                            title: this.$t('general.field.client').toString(),
                            value: this.job.clientId,
                            required: true,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'techspec',
                            type: 'integer',
                            title: this.$t('general.field.techspec').toString(),
                            value: this.job.techspecId,
                            required: true,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.techspecService.techspecFormOptionsCallback(search, this.getCurrentClientId()),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.techspecService.techspecFormTitlesCallback(values),
                            dependencies: {
                                client: /[0-9]+/,
                            },
                            disableNotHide: true,
                        },
                        {
                            key: 'parentJob',
                            type: 'integer',
                            title: this.$t('job.field.parent_job').toString(),
                            value: this.job.parentJobId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobFormOptionsCallback(search, this.getCurrentTechspecId()),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobFormTitlesCallback(values, null),
                            dependencies: {
                                techspec: /[0-9]+/,
                            },
                            disableNotHide: true,
                        },
                        {
                            key: 'clientUser',
                            type: 'integer',
                            title: this.$t('general.field.client_user').toString(),
                            value: this.job.clientUserId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientUserFormOptionsCallback(search, this.getCurrentClientId()),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientUserFormTitlesCallback(values, null),
                            dependencies: {
                                client: /[0-9]+/,
                            },
                            disableNotHide: true,
                        },
                        {
                            key: 'name',
                            type: 'string',
                            title: this.$t('general.field.name').toString(),
                            value: this.job.name,
                            required: true,
                        },
                        {
                            key: 'description',
                            type: 'string',
                            title: this.$t('general.field.description').toString(),
                            value: this.job.description,
                            required: true,
                            multiline: true,
                            rich: true,
                            markdown: true,
                        },
                        {
                            key: 'attachment1',
                            type: 'file',
                            title: this.$t('job.field.attachment1').toString(),
                            value: this.job.attachment1,
                            required: false,
                            emitDownload: true,
                        },
                        {
                            key: 'attachment2',
                            type: 'file',
                            title: this.$t('job.field.attachment2').toString(),
                            value: this.job.attachment2,
                            required: false,
                            emitDownload: true,
                        },
                        {
                            key: 'attachment3',
                            type: 'file',
                            title: this.$t('job.field.attachment3').toString(),
                            value: this.job.attachment3,
                            required: false,
                            emitDownload: true,
                        },
                        {
                            key: 'attachment4',
                            type: 'file',
                            title: this.$t('job.field.attachment4').toString(),
                            value: this.job.attachment4,
                            required: false,
                            emitDownload: true,
                        },
                        {
                            key: 'attachment5',
                            type: 'file',
                            title: this.$t('job.field.attachment5').toString(),
                            value: this.job.attachment5,
                            required: false,
                            emitDownload: true,
                        },
                        {
                            key: 'tags',
                            type: 'string',
                            title: this.$t('general.field.tags').toString(),
                            value: this.job.tags,
                            required: false,
                            multiple: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.tagService.tagFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.tagService.tagFormTitlesCallback(values),
                            allowNewOptions: true,
                        },
                        {
                            key: 'jiraLink',
                            type: 'string',
                            title: this.$t('job.field.jira_link').toString(),
                            value: this.job.jiraLink,
                            required: false,
                        },
                        {
                            key: 'type',
                            type: 'string',
                            title: this.$t('general.field.type').toString(),
                            value: this.job.type,
                            required: true,
                            options: JobService.typeOptions,
                            dropdown: true,
                        },
                        {
                            key: 'priority',
                            type: 'integer',
                            title: this.$t('job.field.priority').toString(),
                            value: this.job.priority,
                            required: true,
                            options: JobService.priorityOptions,
                            dropdown: true,
                        },
                        {
                            key: 'stage',
                            type: 'integer',
                            title: this.$t('job.field.stage').toString(),
                            value: this.job.stageId,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobStageFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobStageFormTitlesCallback(values),
                        },
                        {
                            key: 'assignee',
                            type: 'string',
                            title: this.$t('job.field.assigned_to').toString(),
                            value: this.job.assigneeId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.userService.formOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.userService.formTitlesCallback(values),
                        },
                        {
                            key: 'amount',
                            type: 'decimal',
                            title: this.$t('job.field.amount').toString(),
                            value: this.job.amount,
                            required: false,
                        },
                        {
                            key: 'ticket',
                            type: 'string',
                            title: this.$t('job.field.ticket').toString(),
                            value: this.job.ticket,
                            required: false,
                            options: JobService.ticketOptions,
                            row: true,
                        },
                        {
                            key: 'chasePartner',
                            type: 'boolean',
                            title: this.$t('job.field.chase_partner').toString(),
                            value: this.job.chasePartner,
                            required: true,
                        },
                        {
                            key: 'clientVisible',
                            type: 'boolean',
                            title: this.$t('job.field.client_visible').toString(),
                            value: this.job.clientVisible,
                            required: true,
                        },
                    ],
                    required: true,
                },
                {
                    title: this.$t('general.field.schedule').toString(),
                    fields: [
                        {
                            key: 'dateCreated',
                            type: 'datetime',
                            title: this.$t('general.field.date_created').toString(),
                            value: this.job.dto.dateCreated ? this.job.dateCreated : null,
                            required: false,
                            readonly: true,
                        },
                        {
                            key: 'startDate',
                            type: 'datetime',
                            title: this.$t('job.field.start_date').toString(),
                            value: this.job.startDate,
                            required: false,
                        },
                        {
                            key: 'deadline',
                            type: 'datetime',
                            title: this.$t('general.field.deadline').toString(),
                            value: this.job.deadline,
                            required: false,
                        },
                        {
                            key: 'dateScheduled',
                            type: 'datetime',
                            title: this.$t('job.field.date_scheduled').toString(),
                            value: this.job.dateScheduled,
                            required: false,
                        },
                    ],
                },
                {
                    title: this.$t('general.field.categorisation').toString(),
                    fields: [
                        {
                            key: 'category',
                            type: 'integer',
                            title: this.$t('job.field.category').toString(),
                            value: this.job.categoryId,
                            required: false,
                            optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobCategoryFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobCategoryFormTitlesCallback(values),
                        },
                        {
                            key: 'reason',
                            type: 'string',
                            title: this.$t('job.field.reason').toString(),
                            value: this.job.reason,
                            required: false,
                            options: JobService.reasonOptions,
                            dropdown: true,
                        },
                        {
                            key: 'customerSatisfactionLevel',
                            type: 'string',
                            title: this.$t('job.field.customer_satisfaction_level').toString(),
                            value: this.job.customerSatisfactionLevel,
                            required: false,
                            options: JobService.levelOptions,
                            dropdown: true,
                        },
                        {
                            key: 'roiLevel',
                            type: 'string',
                            title: this.$t('job.field.roi_level').toString(),
                            value: this.job.roiLevel,
                            required: false,
                            options: JobService.levelOptions,
                            dropdown: true,
                        },
                        {
                            key: 'roiAmount',
                            type: 'decimal',
                            title: this.$t('job.field.roi_amount').toString(),
                            value: this.job.roiAmount,
                            required: false,
                        },
                    ],
                },
            ],
            stepper: true,
            cancelButton: this.cancelButton,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the job from the form
     */

    private generateJobFromForm(form: FormInterface): Job
    {
        if (!this.job)
        {
            return new Job(new JobDto());
        }

        const job = new Job(cloneDeep(this.job.dto));

        this.addFieldValues(job, form.items);

        return job;
    }

    /**
     * Adds field values from form items to the passed job
     *
     * @param job
     * @param items
     */

    private addFieldValues(job: Job, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(job, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'client':
                job.clientId = item.value as number;
                break;

            case 'techspec':
                job.techspecId = item.value as number;
                break;

            case 'parentJob':
                job.parentJobId = item.value as number;
                break;

            case 'clientUser':
                job.clientUserId = item.value as number;
                break;

            case 'name':
                job.name = item.value as string;
                break;

            case 'description':
                job.description = item.value as string;
                break;

            case 'attachment1':
                job.attachment1 = item.value as AttachmentInterface;
                break;

            case 'attachment2':
                job.attachment2 = item.value as AttachmentInterface;
                break;

            case 'attachment3':
                job.attachment3 = item.value as AttachmentInterface;
                break;

            case 'attachment4':
                job.attachment4 = item.value as AttachmentInterface;
                break;

            case 'attachment5':
                job.attachment5 = item.value as AttachmentInterface;
                break;

            case 'tags':
                job.tags = item.value as string[];
                break;

            case 'jiraLink':
                job.jiraLink = item.value as string;
                break;

            case 'type':
                job.type = item.value as string;
                break;

            case 'priority':
                job.priority = item.value as number;
                break;

            case 'stage':
                job.stageId = item.value as number;
                break;

            case 'assignee':
                job.assigneeId = item.value as string;
                break;

            case 'amount':
                job.amount = item.value as string;
                break;

            case 'ticket':
                job.ticket = item.value as string;
                break;

            case 'chasePartner':
                job.chasePartner = item.value as boolean;
                break;

            case 'clientVisible':
                job.clientVisible = item.value as boolean;
                break;

            case 'startDate':
                job.startDate = item.value as Date;
                break;

            case 'deadline':
                job.deadline = item.value as Date;
                break;

            case 'dateScheduled':
                job.dateScheduled = item.value as Date;
                break;

            case 'category':
                job.categoryId = item.value as number;
                break;

            case 'reason':
                job.reason = item.value as string;
                break;

            case 'customerSatisfactionLevel':
                job.customerSatisfactionLevel = item.value as string;
                break;

            case 'roiLevel':
                job.roiLevel = item.value as string;
                break;

            case 'roiAmount':
                job.roiAmount = item.value as string;
                break;
            }
        }
    }

    /**
     * Returns the currently selected client id
     *
     * @returns
     */

    private getCurrentClientId(): number|null
    {
        return this.fieldValues.client ? this.fieldValues.client as number : null;
    }

    /**
     * Returns the currently selected techspec id
     *
     * @returns
     */

    private getCurrentTechspecId(): number|null
    {
        return this.fieldValues.techspec ? this.fieldValues.techspec as number : null;
    }

    /**
     * Handles field value changes from the form
     *
     * @param fieldValues
     */

    private onFieldValuesChanged(fieldValues: FormFieldValuesInterface)
    {
        this.fieldValues = Object.assign({}, cloneDeep(fieldValues));
    }

    /**
     * Handles file download requests
     *
     * @param attachment
     */

    private onDownload(attachment: AttachmentInterface)
    {
        this.$serviceContainer.jobService.downloadAttachment(attachment);
    }
}